import { api } from './api'

interface TemplateData {
  id?: string
  name: string
  slides: any[]
  metadata?: {
    createdAt: string
    updatedAt: string
  }
}

const jsonBlob = (value: unknown): Blob =>
  new Blob([JSON.stringify(value)], { type: 'application/json' })

class TemplateService {
  async saveTemplate(templateData: Omit<TemplateData, 'id' | 'metadata'>): Promise<void> {
    try {
      // Slides go up as a file, so a deck's worth of JSON never rides in the body.
      const form = new FormData()
      form.append('metadata', JSON.stringify({ name: templateData.name }))
      form.append('slides', jsonBlob(templateData.slides || []), 'slides.json')

      await api.post('/templates', form)
    }
    catch (error) {
      console.error('Error saving template:', error)
      throw error
    }
  }

  /** Metadata only — the list never needs every template's slide definitions. */
  async loadTemplates(): Promise<TemplateData[]> {
    try {
      const response = await api.get('/templates')

      return response.data.data.map((t: any) => ({
        id: t.id,
        name: t.name,
        slides: [],
        metadata: t.metadata
      }))
    }
    catch (error) {
      console.error('Error loading templates:', error)
      throw error
    }
  }

  async loadTemplate(templateId: string): Promise<TemplateData | null> {
    try {
      const response = await api.get(`/templates/${templateId}`)
      if (!response.data.status || !response.data.data) return null

      const slides = await api.get(`/templates/${templateId}/slides`)
      return {
        id: response.data.data.id,
        name: response.data.data.name,
        slides: (slides.data || []) as any[],
        metadata: response.data.data.metadata
      }
    }
    catch (error) {
      console.error('Error loading template:', error)
      return null
    }
  }

  async updateTemplate(templateId: string, templateData: Partial<TemplateData>): Promise<boolean> {
    try {
      const form = new FormData()
      const metadata: Record<string, unknown> = {}
      if (templateData.name) metadata.name = templateData.name
      form.append('metadata', JSON.stringify(metadata))
      if (templateData.slides) {
        form.append('slides', jsonBlob(templateData.slides), 'slides.json')
      }

      await api.put(`/templates/${templateId}`, form)
      return true
    }
    catch (error) {
      console.error('Error updating template:', error)
      return false
    }
  }

  async deleteTemplate(templateId: string): Promise<boolean> {
    try {
      await api.delete(`/templates/${templateId}`)
      return true
    }
    catch (error) {
      console.error('Error deleting template:', error)
      return false
    }
  }
}

export const templateService = new TemplateService()
export default TemplateService
export type { TemplateData }
